import { CardTx } from "PFApp/components/card_tx/card_tx";
import { ProfileCardConnected } from "PFApp/components/profile_card_connected";
import { useSearchDispatch, useSearchStoreContext } from "PFApp/search/search_context";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { SEARCH_ACTIONS } from "PFReducers/search_reducer/search_reducer";
import PropTypes from "prop-types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./results_all.module.scss";

const Header = ({ name, meta, numberOfItems }) => {
  const dispatch = useSearchDispatch();
  const { t } = useTranslation("search");

  const redirectTab = name === "profiles" ? "matches" : name;

  return (
    <div className={css.header}>
      <Typography withMargin variant="h2">
        {t(`tabs.${name}`)}
      </Typography>
      <div className={css.actions}>
        {meta.total ? t("tabs.show", { number: numberOfItems, of: meta.total }) : t("tabs.noResults")}
        {meta.total > 0 && (
          <Button
            kind="blank"
            className={css.button}
            onClick={() => dispatch({ type: SEARCH_ACTIONS.SEARCH_SET_TAB, payload: redirectTab })}
          >
            {t("tabs.seeAll", { template: t(`tabs.${name}`) })}
          </Button>
        )}
      </div>
    </div>
  );
};

Header.propTypes = {
  name: PropTypes.string,
  meta: PropTypes.shape({
    total: PropTypes.number
  }),
  numberOfItems: PropTypes.number
};

const ResultsAll = () => {
  const {
    search: { matches, matchesMeta, matchesPending, allActivities, availableTemplates, activitiesPending }
  } = useSearchStoreContext();
  const isChatEnabled = useIsChatEnabled();

  const templatesKeys = useMemo(
    () => availableTemplates?.map((activity) => activity.key) || [],
    [availableTemplates]
  );

  const allActivitiesData = useMemo(() => {
    const activities = templatesKeys.reduce(
      (acc, item) => ({
        ...acc,
        [item]: {
          ...allActivities?.[item],
          entries: allActivities?.[item]?.entries.slice(0, 3)
        }
      }),
      {}
    );
    return {
      ...activities
    };
  }, [templatesKeys, allActivities]);

  return (
    <>
      <div>
        <Header name="profiles" meta={matchesMeta} numberOfItems={matches.slice(0, 3).length} />
        {matchesPending && matches.length === 0 && <LoadingDots pageViewEnabled circlesEnabled />}
        <div className={css.results} style={{ opacity: matchesPending ? 0.5 : 1 }}>
          {matches.slice(0, 3).map((profile) => (
            <ProfileCardConnected key={profile.id} profileResponse={profile} withChat={isChatEnabled} />
          ))}
        </div>
      </div>
      {templatesKeys.map((templateKey) => {
        const activitiesData = allActivitiesData[templateKey];

        if (!activitiesData.entries || activitiesData.entries.length === 0) {
          return null;
        }
        return (
          <div key={templateKey}>
            <Header
              name={templateKey}
              meta={allActivitiesData[templateKey].meta}
              numberOfItems={allActivitiesData[templateKey].entries.length}
            />
            <div className={css.results} style={{ opacity: activitiesPending ? 0.5 : 1 }}>
              {allActivitiesData[templateKey].entries.map((item) => (
                <CardTx key={item.id} activity={item.activity} />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default ResultsAll;
